// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.PopupInput.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

/**
 * PopupInput Component
 */

import React, { Component } from 'react';
import { observable } from 'mobx';
import classnames from 'classnames';
import connect from 'share/enhancers/Izenda.Connect';
import Popup from 'share/components/controls/CommonPopup/Izenda.Popup';
import PopupInputUIState from 'logic/models/Izenda.PopupInputUIState';
import { isNil, getOr, invoke } from 'lodash/fp';
import PropTypes from 'prop-types';
import ButtonCheck from 'share/components/controls/Izenda.ButtonCheck';

@connect()
class PopupInputContent extends Component {
  constructor(props: any) {
    super(props);
    const value = props.uiState.value || {};
    const popupContentProps = props.popupContentProps || {};
    const settingsBtn = popupContentProps.settingsBtn || [];

    // Get button state
    const buttonSettingState = {};
    settingsBtn.forEach((btnInfo, index) => {
      const disabledButton = btnInfo.disabledBy && !value[btnInfo.disabledBy];
      buttonSettingState[btnInfo.ref || index] = !!disabledButton;
    });

    this.buttonSettingState = observable(buttonSettingState);
  }

  closePopup = (type: string) => {
    const props = this.props;

    if (type === 'accept') {
      const value = invoke('getValue', this.popupContent);
      Promise.resolve(props.onChange && props.onChange({ value })).finally(() => {
        props.onAccept();
      });
    }

    props.onReject();
  };

  closingPopup = (callback: any, type: string) => {
    const props = this.props;
    const { popupContentProps = {}, validation } = props;
    const popupContent = this.popupContent;

    beforeClosingPopup(type).then(canClose => {
      if (canClose) {
        if (type === 'accept') {
          validation.validate().then(success => {
            if (success) {
              return Promise.resolve(
                props.onChange && props.onChange({ value: popupContent.getValue && popupContent.getValue() })
              ).finally(() => {
                validation.reset();
                props.onAccept();
              });
            }
          });
        } else {
          invoke('resetData', popupContent);
          validation.reset();
          props.onAccept();
        }
      }
    });

    function beforeClosingPopup(btnType: string) {
      const beforeCloseFn = popupContentProps.beforeClosing;

      if (beforeCloseFn && popupContent[beforeCloseFn]) {
        return Promise.resolve(popupContent[beforeCloseFn](validation, btnType));
      }
      return Promise.resolve(true);
    }
  };

  handleConfirm = (index: string) => {
    const { validation } = this.props;
    const settingsBtn = getOr([], 'popupContentProps.settingsBtn', this.props);
    const popupContent = this.popupContent;

    // access popupContent as the time of onClick call not when render first time
    validation.reset();
    const action = settingsBtn[index].action;
    action && popupContent[action] && popupContent[action]();
  };

  getSettingButtons: any = () => {
    const settingsBtn = getOr([], 'popupContentProps.settingsBtn', this.props);

    return settingsBtn.map((btnInfo, index) => (
      <Popup.ConfirmationButton
        disabled={this.buttonSettingState[btnInfo.ref || index]}
        ref={btnInfo.ref}
        key={index}
        {...btnInfo}
        onClick={this.handleConfirm.bind(this, index)}
      />
    ));
  };

  // Notify to change button state
  notifySettingButtonState = (buttonRef: any, disabled: boolean) => {
    this.buttonSettingState[buttonRef] = disabled;
  };

  setDom = (dom: any) => {
    this.popupContent = dom;
  };

  render() {
    const props = this.props;
    const { popupContentProps = {}, localization } = props;
    const confirmButtonLabel = popupContentProps.confirmButtonLabel || localization.getText('COMMON_LABEL_OK');

    return (
      <Popup {...props.popupProps} visible onClosing={this.closingPopup} onClosed={this.closePopup}>
        <Popup.Header title={props.popupTitle || localization.getText('POPUP_INPUT_DEFAULT_TITLE')} />
        <Popup.Body>
          {props.popupContent ? (
            <props.popupContent
              {...props}
              {...popupContentProps}
              settingValue={props.uiState.settingValue}
              value={props.uiState.value}
              notifySettingButtonState={this.notifySettingButtonState}
              ref={this.setDom}
            />
          ) : (
            'empty-popup'
          )}
        </Popup.Body>
        <Popup.Footer>
          {this.getSettingButtons()}
          <Popup.ConfirmationButton type="accept">{confirmButtonLabel}</Popup.ConfirmationButton>
          <Popup.ConfirmationButton type="cancel">
            {localization.getText('COMMON_LABEL_CANCEL')}
          </Popup.ConfirmationButton>
        </Popup.Footer>
      </Popup>
    );
  }
}

@connect()
export default class PopupInput extends Component {
  static propTypes = {
    // Condition to show warning before open popup
    warningCondition: PropTypes.func,
    // Action when user accept the warning
    warningAction: PropTypes.func
  }

  constructor(props: any) {
    super(props);
    this.uiState = new PopupInputUIState();
    this.uiState.update(this.props);
  }

  componentWillUpdate(nextProps: any) {
    this.uiState.update(nextProps);
  }

  onClick = (...args: any) => {
    const props = this.props;

    if (props.onClick) {
      Promise.resolve(props.onClick(args)).then(data => !isNil(data) && this.openPopup());
    } else {
      if (props.warningCondition && props.warningCondition()) {
        return props
          .confirmFn('Confirmation', {
            description: props.warningMessage
          })
          .then(() => {
            props.warningAction && props.warningAction();
            this.openPopup();
          });
      } else {
        this.openPopup();
      }
    }
  };

  openPopup = () => {
    let cloneProps = this.props;
    const uiState = this.uiState;
    !cloneProps.uiState && (cloneProps = Object.assign({}, cloneProps, { uiState }));
    cloneProps.uiState.update(cloneProps);
    cloneProps.openPopup(PopupInputContent, cloneProps);
  };

  onResetValue = () => {
    const props = this.props;
    let defaultValue = null;
    if (props.fnResetDefaultValue) {
      defaultValue = props.fnResetDefaultValue();
    }

    props.onChange && props.onChange(defaultValue);
  };

  render() {
    const props = this.props;
    const { hadSetting } = this.uiState;
    const { localization, label, popupTitle, className } = props;
    const classes = classnames('izenda-PopupInput', props.containerClassName);
    return (
      <div className={classes}>
        <ButtonCheck hasValue={hadSetting}
                     className={className}
                     label={label}
                     onClick={this.onClick}
                     title={popupTitle}
        />
        {hadSetting && (
          <span>
            <i
              className="icon-cancel u-colorRed"
              title={localization.getText('PROPERTIES_EDITOR_REMOVE_SETTING')}
              onClick={this.onResetValue}
            />
          </span>
        )}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/share/components/controls/Izenda.PopupInput.jsx